import { ApiResponse } from '@app/infrastructure/data-list/grid/api-grid/api';
import { IGridColumnConfig } from '@app/infrastructure/data-list/grid/grid-column-config.model';
import { ICustomHeader } from '@app/infrastructure/data-list/grid/grid.model';
import { FilterTypes, SingleSelection } from '@app/infrastructure/filters/filters.model';
import { GridFilterConfig } from '@app/infrastructure/filters/grid-filters/grid-filter.model';

export enum CommitmentTableCells {
	type = 'type',
	isZestySupported = 'isZestySupported',
	start = 'start',
	end = 'end',
	hourlyCommitment = 'hourlyCommitment',
	amortized = 'amortized',
	utilizationPercentage = 'utilizationPercentage',
	savingsPercentage = 'savingsPercentage',
	headerSavings = 'headerSavings',
	savings = 'savings',
}

export const COMMITMENT_COLUMNS: IGridColumnConfig<ICommitmentDTO>[] = [
	{
		name: 'RI/SP',
		prop: 'type',
		headerTemplate: ICustomHeader.sorting,
		cellTemplate: CommitmentTableCells.type,
		width: '75px',
	},
	{
		name: 'Description',
		prop: 'description',
		headerTemplate: ICustomHeader.sorting,
		width: '150px',
	},
	{
		name: 'Supported',
		prop: 'isZestySupported',
		headerTemplate: ICustomHeader.sorting,
		cellTemplate: CommitmentTableCells.isZestySupported,
		width: '110px',
	},
	{
		name: 'Start',
		prop: 'start',
		headerTemplate: ICustomHeader.sorting,
		cellTemplate: CommitmentTableCells.start,
		width: '110px',
	},
	{
		name: 'End',
		prop: 'end',
		headerTemplate: ICustomHeader.sorting,
		cellTemplate: CommitmentTableCells.end,
		width: '120px',
	},
	{
		name: 'Commitment',
		prop: 'hourlyCommitment',
		headerTemplate: ICustomHeader.sorting,
		cellTemplate: CommitmentTableCells.hourlyCommitment,
		width: '120px',
	},
	{
		name: 'Amortized',
		prop: 'amortized',
		headerTemplate: ICustomHeader.sorting,
		cellTemplate: CommitmentTableCells.amortized,
		width: '135px',
	},
	{
		name: 'Utilization',
		prop: 'utilizationPercentage',
		headerTemplate: ICustomHeader.sorting,
		cellTemplate: CommitmentTableCells.utilizationPercentage,
		width: '110px',
	},
	{
		name: 'Discount',
		prop: 'savingsPercentage',
		headerTemplate: ICustomHeader.sorting,
		cellTemplate: CommitmentTableCells.savingsPercentage,
		width: '100px',
	},
	{
		name: 'Savings',
		prop: 'savings',
		headerTemplate: CommitmentTableCells.headerSavings,
		cellTemplate: CommitmentTableCells.savings,
		width: '120px',
	},
];

// For ApiGridFilters: describe the full interface of possible filter options (visible & not)
export interface ICommitmentFilters {
	is_supported: SingleSelection;
	expired: SingleSelection;
	start: SingleSelection;
	end: SingleSelection;
}

// For GridFiltersComponent: describe *visible filter options which user can select and use (visible only)
export const COMMITMENT_FILTERS: GridFilterConfig<ICommitmentFilters>[] = [
	{
		label: 'Supported by',
		prop: 'is_supported',
		type: FilterTypes.StringSelection,
	},
	{
		label: 'Expired',
		prop: 'expired',
		type: FilterTypes.StringSelection,
	},
	{
		label: 'Start',
		prop: 'start',
		type: FilterTypes.StringSelection,
	},
	{
		label: 'End',
		prop: 'end',
		type: FilterTypes.StringSelection,
	},
];

export enum CommitmentFilterNames {
	is_supported = 'is_supported',
	expired = 'expired',
	start = 'start',
	end = 'end',
}

export enum CommitmentTypes {
	RI = 'RI',
	SP = 'SP',
}

export interface ICommitmentTableDTO {
	commitments: ApiResponse<ICommitmentDTO>;
	commitmentsTotals: {
		notExpired: ICommitmentTotalDTO;
		expired: ICommitmentTotalDTO;
	};
}

export type ICommitmentTotalDTO = Pick<
	ICommitmentDTO,
	| 'savings'
	| 'amortized'
	| 'hourlyCommitment'
	| 'savingsPercentage'
	| 'utilizationPercentage'
	| 'isExpired'
	| 'zestySavings'
	| 'zestySavingsPercentage'
	| 'zestyAdditionalSavings'
>;

export interface ICommitmentDTO {
	rowId: number;
	savings: number;
	amortized: number;
	hourlyCommitment: number;
	savingsPercentage: number;
	utilizationPercentage: number;
	isExpired: boolean;
	zestySavings: number;
	zestySavingsPercentage: number | null;
	zestyAdditionalSavings: number | null;
	type: CommitmentTypes;
	description: string;
	end: string;
	start: string;
	isZestySupported: boolean;
	zestyNotSupportedReason: string | null;
}
